import axios from 'axios';

export const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export const api = axios.create({
    baseURL: `${API_URL}/api/v1`,
});

// Request interceptor to add the auth token header to requests
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('access_token');
        if (token) {
            config.headers['Authorization'] = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        Promise.reject(error);
    }
);

// Response interceptor to handle 401 errors
api.interceptors.response.use(
    (response) => response,
    async (error) => {
        // If we get a 401 error, clear the token and redirect to login
        // BUT skip redirect if it's from the login endpoint itself
        if (error.response && error.response.status === 401) {
            const isLoginRequest = error.config?.url?.includes('/auth/login');

            if (!isLoginRequest) {
                // Clear the access token
                localStorage.removeItem('access_token');

                // Redirect to login page
                window.location.href = '/login';
            }
        }

        return Promise.reject(error);
    }
);

export const getPosts = async (params?: any) => {
    const response = await api.get('/portal/posts', {
        params: params
    });
    return response.data;
};

export const getComments = async (postId: string) => {
    const response = await api.get(`/portal/posts/${postId}/comments`);
    return response.data;
};

export const getCommentThread = async (postId: string, commentId: string) => {
    const response = await api.get(`/portal/posts/${postId}/comments/${commentId}`);
    return response.data;
};

export const sendReply = async (commentId: string, replyText: string) => {
    const response = await api.post(`/portal/comments/${commentId}/reply`, null, {
        params: { reply_text: replyText }
    });
    return response.data;
};

export const generateAIReply = async (data: { commentId: string; context?: string }) => {
    const response = await api.post('/ai/generate', data);
    return response.data;
};

export const regenerateAIReply = async (commentId: string, regenerationStyle?: string) => {
    const response = await api.post(`/ai/comment/${commentId}/regenerate`, {
        regeneration_style: regenerationStyle
    });
    return response.data;
};

// Auth API
export const login = async (credentials: any) => {
    const response = await api.post('/auth/login', credentials);
    return response.data;
};

export const signup = async (userData: any) => {
    const response = await api.post('/auth/signup', userData);
    return response.data;
};

export const forgotPassword = async (email: string) => {
    const response = await api.post('/auth/forgot-password', { email });
    return response.data;
};

export const changePassword = async (newPassword: string) => {
    const response = await api.post('/auth/change-password', { new_password: newPassword });
    return response.data;
};

export const getMe = async () => {
    const response = await api.get('/auth/me');
    return response.data;
};

export const updateMe = async (userData: { name?: string; first_name?: string; last_name?: string }) => {
    const response = await api.patch('/auth/me', userData);
    return response.data;
};

export const getMyPermissions = async () => {
    const response = await api.get('/auth/my-permissions');
    return response.data;
};

// Settings API
export const saveToken = async (tokenData: any) => {
    const response = await api.post('/settings/tokens', tokenData);
    return response.data;
};

export const getTokens = async () => {
    const response = await api.get('/settings/tokens');
    return response.data;
};

export const deleteToken = async (id: number) => {
    const response = await api.delete(`/settings/tokens/${id}`);
    return response.data;
};

// Portal Users API
export const getUsers = async (skip: number = 0, limit: number = 20) => {
    const response = await api.get('/portal/users', {
        params: { skip, limit }
    });
    return response.data;
};

export const resetUserPassword = async (userId: number) => {
    const response = await api.post(`/portal/users/${userId}/reset-password`);
    return response.data;
};

export const deleteUser = async (userId: number) => {
    const response = await api.delete(`/portal/users/${userId}`);
    return response.data;
};

// Analytics API
export const getDashboardAnalytics = async (params?: {
    start_date?: string;
    end_date?: string;
    search_query?: string;
    platform?: string;
    timezone_offset?: number;
}) => {
    const response = await api.get('/analytics/dashboard', { params });
    return response.data;
};

export const getPostsAnalytics = async (params?: {
    search_query?: string;
    platform?: string;
    show_hidden?: boolean;
    skip?: number;
    limit?: number;
}) => {
    const response = await api.get('/analytics/posts-analytics', { params });
    return response.data;
};

// Comments API
export const getAllComments = async (params?: {
    search_query?: string;
    platform?: string;
    sentiment?: string;
    skip?: number;
    limit?: number;
}) => {
    const response = await api.get('/portal/comments', { params });
    return response.data;
};

export const getAIResponses = async (params?: {
    search_query?: string;
    platform?: string;
    post_id?: string;
    sentiment?: string;
    bookmarked?: boolean | null;
    posted?: boolean;
    show_hidden?: boolean;
    start_date?: string;
    end_date?: string;
    timezone_offset?: number;
    skip?: number;
    limit?: number;
}) => {
    const response = await api.get('/portal/ai-responses', { params });
    return response.data;
};

export const postAIResponse = async (commentId: string) => {
    const response = await api.post(`/portal/comments/${commentId}/post-ai-response`);
    return response.data;
};

export const updatePost = async (postId: string, data: { isHidden?: boolean }) => {
    const response = await api.patch(`/portal/posts/${postId}`, data);
    return response.data;
};

export const updateComment = async (commentId: string, data: { aiResponseEdited?: string; isHidden?: boolean; isBookmarked?: boolean; aiSentimentEdited?: string; sentimentEditedBy?: string }) => {
    const response = await api.patch(`/portal/comments/${commentId}`, data);
    return response.data;
};

export const deleteAIResponse = async (commentId: string) => {
    const response = await api.delete(`/portal/comments/${commentId}/response`);
    return response.data;
};

export const submitFeedback = async (commentId: string, data: { vote_type: 'upvote' | 'downvote' | 'none', reason_code?: string, custom_reason?: string }) => {
    const response = await api.post(`/portal/comments/${commentId}/feedback`, data);
    return response.data;
};

export const addTagsToComment = async (commentId: string, tags: string[]) => {
    const response = await api.post(`/portal/comments/${commentId}/tags`, { tags });
    return response.data;
};

export const getCommentTags = async (commentId: string) => {
    const response = await api.get(`/portal/comments/${commentId}/tags`);
    return response.data;
};

export const syncPost = async (postId: string, force: boolean = false, platform: string = 'INSTAGRAM', analyzeMedia: boolean = true, generateAi: boolean = true) => {
    let basePlatform = platform.toLowerCase();
    // Normalize platform parameter since db can store it as 'Twitter'
    if (basePlatform === 'twitter' || basePlatform === 'x') {
        basePlatform = 'twitter';
    } else if (basePlatform === 'tiktok') {
        basePlatform = 'tiktok';
    } else if (basePlatform === 'facebook') {
        basePlatform = 'facebook';
    } else {
        basePlatform = 'instagram';
    }

    const endpoint = `/sync/${basePlatform}/post/${postId}`;
    const response = await api.post(endpoint, null, {
        params: {
            force,
            analyze_media: analyzeMedia,
            generate_ai: generateAi
        }
    });
    return response.data;
};

export const triggerTikTokSync = async (force: boolean = true) => {
    const response = await api.post('/sync/tiktok', null, {
        params: { force }
    });
    return response.data;
};

export const triggerInstagramSync = async (force: boolean = true) => {
    const response = await api.post('/sync/instagram', null, {
        params: { force }
    });
    return response.data;
};

export const triggerTikTokPostsOnlySync = async (force: boolean = true) => {
    const response = await api.post('/sync/tiktok/posts', null, {
        params: { force }
    });
    return response.data;
};

export const triggerInstagramPostsOnlySync = async (force: boolean = true) => {
    const response = await api.post('/sync/instagram/posts', null, {
        params: { force }
    });
    return response.data;
};

export const triggerTwitterPostsOnlySync = async (force: boolean = true) => {
    const response = await api.post('/sync/twitter/tweets', null, {
        params: { force }
    });
    return response.data;
};

export const triggerInstagramTaggedPostsSync = async (force: boolean = true, pageUsername?: string) => {
    const response = await api.post('/sync/instagram/tagged', null, {
        params: {
            force,
            page_username: pageUsername,
            analyze_media: true,
            generate_ai: true
        }
    });
    return response.data;
};

export const getTikTokAuthUrl = async () => {
    const response = await api.get('/auth/tiktok/auth-url');
    return response.data;
};

export const getPostComments = async (platform: string, postId: string) => {
    const response = await api.get(`/ai/comments-by-platform/${platform}/${postId}`);
    return response.data;
};

export const getAIResponseStatistics = async (params?: {
    start_date?: string;
    end_date?: string;
    platform?: string;
    scope?: string;
    timezone_offset?: number;
}) => {
    const response = await api.get('/analytics/ai-response-statistics', { params });
    return response.data;
};

export const getAssignmentReport = async (params?: {
    scope?: string;
    start_date?: string;
    end_date?: string;
    platform?: string;
    timezone_offset?: number;
}) => {
    const response = await api.get('/analytics/assignment-report', { params });
    return response.data;
};

export const getPortalUsers = async (params?: {
    tenant_id?: number;
}) => {
    const response = await api.get('/portal/users', { params });
    return response.data;
};

export const assignCommentToUser = async (commentId: string, userId?: string) => {
    // Use the existing updateComment endpoint but with assignment data
    const response = await api.patch(`/portal/comments/${commentId}`, {
        assigned_user_id: userId || null,
        assignment_action: userId ? 'assign' : 'unassign',
        assignment_timestamp: new Date().toISOString()
    });
    return response.data;
};

export const triggerFacebookSync = async (force: boolean = true) => {
    const response = await api.post('/sync/facebook', null, {
        params: { force }
    });
    return response.data;
};

export const triggerFacebookPostsOnlySync = async (force: boolean = true) => {
    const response = await api.post('/sync/facebook/posts', null, {
        params: { force }
    });
    return response.data;
};

